import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChatCircleDots, CheckCircle, ArrowCounterClockwise, Trash, IdentificationBadge } from "@phosphor-icons/react";
import { adminApi, getErrorMessage } from "@/lib/api";
import { TicketContact } from "./TicketContact";
import { TicketAttachments } from "./TicketAttachments";
import { TicketNotes } from "./TicketNotes";

export const TICKET_STATUS = {
  in_progress: { label: "In Progress", cls: "bg-amber-500/10 text-amber-400 border-amber-500/25" },
  completed: { label: "Completed", cls: "bg-[#73bf44]/10 text-[#73bf44] border-[#73bf44]/25" },
};

export const refreshBadges = () => window.dispatchEvent(new Event("otm:badges-refresh"));

export function ago(iso) {
  if (!iso) return "";
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${Math.max(mins, 1)}m ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
}

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { timeZone: "Australia/Brisbane", dateStyle: "medium", timeStyle: "short" }) : "—");

export function TicketCard({ t, onChanged, onDeleted }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const st = TICKET_STATUS[t.status] || TICKET_STATUS.in_progress;
  const setStatus = async (status) => {
    setBusy(true);
    try {
      onChanged((await adminApi.patch(`/admin/support-tickets/${t.id}`, { status })).data);
      refreshBadges();
      toast.success(status === "completed" ? `${t.reference} marked completed` : `${t.reference} moved back to In Progress`);
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm(`Delete ticket ${t.reference}? This can't be undone.`)) return;
    try { await adminApi.delete(`/admin/support-tickets/${t.id}`); onDeleted(t.id); refreshBadges(); toast.success("Ticket deleted"); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };
  return (
    <article data-testid="support-ticket-card" data-ticket-ref={t.reference} className="bg-[#0F0F0F] border border-neutral-800 rounded-xl p-5 hover:border-neutral-700 transition-colors">
      <header className="flex flex-wrap items-center gap-2.5 mb-4">
        <span className="font-mono text-sm font-bold text-white" data-testid="support-ticket-reference">{t.reference}</span>
        <span className={`text-[0.65rem] font-semibold px-2.5 py-0.5 rounded-full border ${st.cls}`} data-testid="support-ticket-status">{st.label}</span>
        {t.source === "contact" && (
          <span className="text-[0.6rem] uppercase tracking-wider font-semibold text-sky-300 border border-sky-500/25 bg-sky-500/10 rounded-full px-2 py-0.5" data-testid="support-ticket-source">Contact page</span>
        )}
        <span className="text-xs text-neutral-500" title={fmt(t.created_at)}>{ago(t.created_at)}</span>
        <div className="ml-auto flex items-center gap-1">
          {t.status === "in_progress" ? (
            <button disabled={busy} onClick={() => setStatus("completed")} data-testid="ticket-mark-completed-btn" className="inline-flex items-center gap-1.5 bg-[#73bf44] text-[#050505] rounded-full font-semibold px-3.5 py-1.5 text-xs hover:bg-[#5fa338] transition-colors disabled:opacity-50">
              <CheckCircle size={14} weight="fill" /> Mark completed
            </button>
          ) : (
            <button disabled={busy} onClick={() => setStatus("in_progress")} data-testid="ticket-reopen-btn" className="inline-flex items-center gap-1.5 border border-neutral-700 text-neutral-300 rounded-full px-3.5 py-1.5 text-xs hover:text-white hover:border-neutral-500 transition-colors disabled:opacity-50">
              <ArrowCounterClockwise size={14} /> Move back to In Progress
            </button>
          )}
          <button onClick={del} title="Delete ticket" data-testid="ticket-delete-btn" className="p-2 rounded-lg text-neutral-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"><Trash size={15} /></button>
        </div>
      </header>
      <div className="grid md:grid-cols-[minmax(0,220px)_1fr] gap-5">
        <div className="space-y-2 text-sm">
          <div className="text-white font-medium" data-testid="support-ticket-name">{t.name}</div>
          <div className="flex items-center gap-1.5 text-xs">
            <IdentificationBadge size={14} className="text-neutral-500" />
            <span className="font-mono text-[#73bf44]">{t.chassis_number}</span>
            {!t.caravan_found && <span className="text-[0.6rem] text-amber-400">(not in database)</span>}
          </div>
          {t.model && <div className="text-xs text-neutral-500">{t.model}</div>}
          <TicketContact t={t} />
          {t.conversation_id && (
            <button onClick={() => navigate(`/admin/conversations/${t.conversation_id}`)} data-testid="ticket-view-convo-link" className="flex items-center gap-1.5 text-xs text-sky-300 hover:underline">
              <ChatCircleDots size={14} /> View conversation
            </button>
          )}
          <div className="text-[0.65rem] text-neutral-600 pt-1">Lodged {fmt(t.created_at)}{t.completed_at ? ` · Completed ${fmt(t.completed_at)}` : ""}</div>
        </div>
        <div className="space-y-3 min-w-0">
          <div className="border-l-2 border-[#73bf44] bg-[#73bf44]/[0.04] rounded-r-lg px-3.5 py-2.5">
            <div className="text-[0.62rem] uppercase tracking-[0.18em] font-bold text-neutral-500 mb-1">{t.source === "contact" ? "Issue described by customer" : "Summary"}</div>
            <p className="text-sm text-neutral-200 leading-relaxed whitespace-pre-wrap" data-testid="support-ticket-summary">{t.summary}</p>
          </div>
          {t.note && (
            <div className="px-3.5">
              <div className="text-[0.62rem] uppercase tracking-[0.18em] font-bold text-neutral-500 mb-1">Customer note</div>
              <p className="text-sm text-neutral-300 whitespace-pre-wrap" data-testid="support-ticket-note">{t.note}</p>
            </div>
          )}
          <TicketAttachments t={t} />
          <TicketNotes t={t} onSaved={onChanged} />
        </div>
      </div>
    </article>
  );
}
